// Tiny start/stop rAF loop used by GameScene and LessonScene. Pulled out so
// the scene files stay inside the 200-line cap and so the loop's idempotency
// contract is captured in one place.
//
// It also owns the tab-visibility pause both scenes used to hand-roll
// identically: a hidden tab gets no frames (browsers throttle rAF there
// anyway, but stopping is explicit and cheaper), and coming back fires
// `onResume` so the caller can mark its canvas dirty — nothing repainted while
// the tab was away, so the first frame back has to be a real one.

export class RafLoop {
  private id: number | null = null;
  private running = false;

  private readonly onVisibilityChange = (): void => {
    if (!this.running) return;
    if (document.hidden) {
      this.cancelFrame();
      return;
    }
    this.onResume?.();
    this.requestFrame();
  };

  constructor(
    private readonly tick: () => void,
    private readonly onResume?: () => void,
  ) {}

  start(): void {
    if (this.running) return;
    this.running = true;
    document.addEventListener('visibilitychange', this.onVisibilityChange);
    if (!document.hidden) this.requestFrame();
  }

  stop(): void {
    this.running = false;
    document.removeEventListener('visibilitychange', this.onVisibilityChange);
    this.cancelFrame();
  }

  private requestFrame(): void {
    if (this.id !== null) return;
    const frame = (): void => {
      this.id = requestAnimationFrame(frame);
      this.tick();
    };
    this.id = requestAnimationFrame(frame);
  }

  private cancelFrame(): void {
    if (this.id === null) return;
    cancelAnimationFrame(this.id);
    this.id = null;
  }
}
